/**
 * Deal Calculator Modal Component
 *
 * Real-time deal calculation with 4 input boxes:
 * - Vehicle Price
 * - Trade-In Value
 * - Down Payment
 * - Credit Tier
 *
 * Displays calculated monthly payment, total cost, APR, and payment breakdown.
 */

import {
  CalculatorIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';

interface DealCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehiclePrice?: number;
  vehicleInfo?: {
    year?: number;
    make?: string;
    model?: string;
  };
}

// Serialized services.deal_calculator.DealFinancials (WS3 Task 8) -- the ONE
// preview endpoint's response shape. Replaces the old legacy nested
// fees/calculations/recommendations shape that the former E2 duplicate engine
// (deleted) used to return.
interface FeeItemized {
  name: string;
  amount: string;
  taxable: boolean;
}

interface DealPreviewResponse {
  amount_financed: number;
  sales_tax: number;
  fees_total: number;
  fees_itemized: FeeItemized[];
  payment_amount: number;
  finance_charge: number;
  total_of_payments: number;
  total_sale_price: number;
  apr: number | null;
  term: number | null;
  recommended_down_payment: number | null;
  recommended_down_payment_pct: number | null;
}

const DealCalculatorModal: React.FC<DealCalculatorModalProps> = ({
  isOpen,
  onClose,
  vehiclePrice = 0,
  vehicleInfo,
}) => {
  // Input state
  const [price, setPrice] = useState<string>(vehiclePrice > 0 ? vehiclePrice.toString() : '');
  const [tradeIn, setTradeIn] = useState<string>('0');
  const [downPayment, setDownPayment] = useState<string>('0');
  const [creditTier, setCreditTier] = useState<string>('subprime');
  const [termMonths, setTermMonths] = useState<number>(36);

  // Results state
  const [result, setResult] = useState<DealPreviewResponse | null>(null);
  const [calculating, setCalculating] = useState(false);

  // Credit tier options
  const creditTiers = [
    { value: 'prime', label: 'Prime (720+ FICO)', color: 'text-green-600' },
    { value: 'near_prime', label: 'Near Prime (660-719)', color: 'text-blue-600' },
    { value: 'subprime', label: 'Subprime (620-659)', color: 'text-yellow-600' },
    { value: 'deep_subprime', label: 'Deep Subprime (580-619)', color: 'text-orange-600' },
    { value: 'special_finance', label: 'Special Finance (<580)', color: 'text-red-600' },
  ];

  // Term options
  const termOptions = [12, 18, 24, 30, 36, 48, 60];

  // Update vehicle price when prop changes
  useEffect(() => {
    if (vehiclePrice > 0) {
      setPrice(vehiclePrice.toString());
    }
  }, [vehiclePrice]);

  const handleCalculate = useCallback(async () => {
    setCalculating(true);

    try {
      // WS3 Task 8: THE ONE preview endpoint. deal_type omitted -- defaults to
      // bhph (api.v1.endpoints.deal_contracting._normalize_deal_type), matching
      // this widget's prior implicit behavior. credit_tier is a back-compat
      // fallback the endpoint uses to derive apr when apr itself is omitted.
      const response = await apiService.post<DealPreviewResponse>('/deals/calculate', {
        vehicle_price: parseFloat(price) || 0,
        trade_in_allowance: parseFloat(tradeIn) || 0,
        down_payment: parseFloat(downPayment) || 0,
        credit_tier: creditTier,
        term_periods: termMonths,
        frequency: 'monthly',
      });

      setResult(response);
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate deal');
    } finally {
      setCalculating(false);
    }
  }, [creditTier, downPayment, price, termMonths, tradeIn]);

  // Auto-calculate when inputs change (debounced)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (price && parseFloat(price) > 0) {
        handleCalculate();
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [price, tradeIn, downPayment, creditTier, termMonths, handleCalculate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CalculatorIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Deal Calculator</h2>
            {vehicleInfo && (
              <span className="text-sm text-gray-500">
                {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Inputs */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CurrencyDollarIcon className="h-5 w-5 mr-2 text-blue-600" />
                Deal Inputs
              </h3>

              {/* Vehicle Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Price
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Trade-In Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trade-In Value
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    value={tradeIn}
                    onChange={(e) => setTradeIn(e.target.value)}
                    className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Down Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Down Payment</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    value={downPayment}
                    onChange={(e) => setDownPayment(e.target.value)}
                    className="pl-7 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="0"
                  />
                </div>
                {result &&
                  result.recommended_down_payment !== null &&
                  parseFloat(downPayment || '0') < result.recommended_down_payment && (
                    <p className="mt-1 text-sm text-orange-600">
                      Recommended minimum: {formatCurrency(result.recommended_down_payment)} (
                      {result.recommended_down_payment_pct}%)
                    </p>
                  )}
              </div>

              {/* Credit Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Tier</label>
                <select
                  value={creditTier}
                  onChange={(e) => setCreditTier(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {creditTiers.map((tier) => (
                    <option key={tier.value} value={tier.value}>
                      {tier.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Term Length */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Term Length
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {termOptions.map((months) => (
                    <button
                      key={months}
                      onClick={() => setTermMonths(months)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        termMonths === months
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {months} mo
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-green-600" />
                Calculated Results
              </h3>

              {calculating ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  {/* Monthly Payment - Featured */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200">
                    <div className="text-sm text-blue-700 font-medium mb-1">Monthly Payment</div>
                    <div className="text-4xl font-bold text-blue-900">
                      {formatCurrency(result.payment_amount)}
                    </div>
                    <div className="text-sm text-blue-600 mt-1">
                      {result.apr}% APR for {result.term} months
                    </div>
                  </div>

                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 font-medium mb-1">Amount Financed</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatCurrency(result.amount_financed)}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 font-medium mb-1">Total Interest</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatCurrency(result.finance_charge)}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-xs text-gray-600 font-medium mb-1">Total Payments</div>
                      <div className="text-xl font-semibold text-gray-900">
                        {formatCurrency(result.total_of_payments)}
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <div className="text-xs text-green-700 font-medium mb-1">Total Cost</div>
                      <div className="text-xl font-semibold text-green-900">
                        {formatCurrency(result.total_sale_price)}
                      </div>
                    </div>
                  </div>

                  {/* Fees Breakdown -- itemized straight from the tenant's
                      configured deal_calculation_settings, via the canonical
                      engine (no more hardcoded title/doc/registration buckets). */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 mb-3">Fees & Taxes</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sales Tax</span>
                        <span className="font-medium">{formatCurrency(result.sales_tax)}</span>
                      </div>
                      {result.fees_itemized.map((fee) => (
                        <div className="flex justify-between" key={fee.name}>
                          <span className="text-gray-600">{fee.name}</span>
                          <span className="font-medium">
                            {formatCurrency(parseFloat(fee.amount))}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between pt-2 border-t border-gray-300">
                        <span className="font-semibold text-gray-900">Total Fees</span>
                        <span className="font-semibold">{formatCurrency(result.fees_total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <CalculatorIcon className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-gray-500">Enter vehicle price to calculate deal</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
          {result && (
            <button
              onClick={() => {
                // TODO: Save calculation to deal
                toast.success('Deal calculation saved!');
                onClose();
              }}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save to Deal
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealCalculatorModal;
